import React, {useRef, useState} from 'react'
import cx from 'classnames'

/* Cosmos */
import Price from '@cosmos/Price'
import Button from '@cosmos/button'

/* Config */
import appConfig from '../../../../appConfig'

/* Utils */
import {getProductFinalPrice, updateLocation} from '../../../../modules/product/productDesktopUtils'
import {generateUrlFromRoutePath} from '../../../../core/route/routeUtils'
import {yieldToMain} from '../../../../utils/performance'
import { generateExperiments } from '../../../../modules/analytics/analyticsUtils'

/* Constants */
import {PLATFORMS, ROUTE_PATHS} from '../../../../core/route/routeConstants'
import {DAVINCI_EVENT_NAMES} from '../../../../modules/analytics/davinci/davinciConstants'
import { CART_ACTION_STATE } from '../../../../modules/cart/cartConstants'
import { MODAL_IDS } from '../../../../modules/modal/modalConstants'

/* Actions */
import {actionCreators as pendingActionCreators} from '../../../../redux/actions/pendingActions'
import {actionCreators as productActionCreators} from '../../../../redux/actions/productActions'
import {actionCreators as modalActionCreators} from '../../../../redux/actions/modalActions'

/* Styles */
import s from './other-merchant-box.scss'

const OtherMerchantBox = ({
  dispatch,
  match,
  sku,
  listing,
  delivery,
  type,
  position,
  pageTypeForDavinci,
  isDrawer,
  listings,
  campaign,
  coupon,
  categories,
  imageUrl,
  name,
  listingId,
  categoryId,
  categoryName,
  merchantId,
  merchantName,
  productId,
  customerReviewCount,
  customerReviewScore,
  price,
  categoryUrlKeyword,
  allListings,
  reviewRate,
  reviewCount
}) => {
  const [isOpenCampaign, setIsOpenCampaign] = useState(false)
  const [isOpenCoupon, setIsOpenCoupon] = useState(false)
  const timerRef = useRef(null)
  const deliveryOption = delivery?.find((item) => item?.merchantId === listing?.merchantId)

  const handleMerchantProduct = async(merchantName) => {
    window?.hepsiBus?.track(DAVINCI_EVENT_NAMES.LINK_CLICK, {
      position: position,
      page_type: pageTypeForDavinci,
      url: '',
      placement:
        type === 'column' ? 'other seller bottom sheet' : 'other seller',
      page_value: sku,
      text: 'Diğer Satıcılar',
      location: 'ürüne git',
    })
    await yieldToMain()

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('magaza')) {
      urlParams.delete('magaza');
    }
    const newQueryString = urlParams.toString();

    updateLocation(
      {
        pathname: generateUrlFromRoutePath(
          ROUTE_PATHS[PLATFORMS.DESKTOP].PRODUCT_DETAIL_PAGE_WITH_PRODUCT_SKU,
          {
            ...match.params,
            productDetailUrlKeyword: match?.params?.productDetailUrlKeyword,
            pdpPath: 'p',
            productId: sku,
          }
        ),
        search: `?magaza=${encodeURIComponent(merchantName)}&${newQueryString}`,
      },
      {
        isReplace: true
      },
      dispatch
    )
  }

  const getPrice = (listing) => {
    let priceMap = {
      currentPriceClassName: s.currentPrice,
      currency: 'TL',
      showDiscountRate: false,
      isNewProductStyle: true,
      showDiscountRateIcon: false,
      isInStock: true,
      hideDiscountWrapper: true,
      ...isDrawer && {hideDiscountWrapper: false}
    }

    if (isDrawer && listing?.prices?.length > 1) {
      priceMap.prevPrice = listing?.prices?.[0]?.formattedPrice;
      priceMap.prevPriceRaw = listing?.prices?.[0]?.value;
    }

    priceMap.currentPrice = listing?.prices?.[listing?.prices?.length - 1]?.formattedPrice
    priceMap.currentPriceRaw = listing?.prices?.[listing?.prices?.length - 1]?.value

    return priceMap
  }

  const tooltipStaticData = () => {
    if (isDrawer) {
      return null;
    }

    return (
      <div className={cx(s.tooltip)}>
        <div className={s.tooltipContainer}>
          <div className={s.merchantInfo}>
            <i className={cx(s.trustedIcon, s.tooltipTrustedIcon)}/>
            <span className={s.tooltipTitle}>Yetkili satıcı</span>
          </div>
          <div className={s.tooltipContent}>
            {'Türkiye veya Dünya’da yüksek bilinirliğe sahip markalara ait satıcılara onaylanmış satıcı rozeti verilir.'}
          </div>
        </div>
        <div>
          <div className={s.merchantInfo}>
            <span
              className={cx(s.rating, s[`rating${Math.floor(listing?.merchantInfo?.ratingSummary?.lifetimeRating)}`])}
              data-test-id={'merchant-rating'}>
              {parseFloat(listing?.merchantInfo?.ratingSummary?.lifetimeRating).toFixed(1).
                replace('.', ',')}
            </span>
            <span className={s.tooltipTitle}>Satıcı puanı</span>
          </div>
          <div className={s.tooltipContent}>
            {'Satıcı puanı, müşteri geri bildirimleri ve satıcının sipariş sürecindeki performansı dikkate alınarak hesaplanmaktadır.'}
          </div>
        </div>
      </div>
    );
  }

  const tooltipCoupon = () => {
    if (isDrawer) {
      return null
    }

    return (
      <div
        className={cx(s.tooltip, {
          [s.promoTooltipOpen]: isOpenCampaign || isOpenCoupon,
          [s.openCampaign]: isOpenCampaign,
          [s.openCoupon]: isOpenCoupon,
        })}>
        {
          <div className={cx(s.tooltipContainer, s.promoTooltip)}>
            <div className={s.promoInfoIcon}></div>
            <div className={s.tooltipContent}>
              {'Coupon'}
            </div>
          </div>
        }
      </div>
    );
  }

  const tooltipCampaign = () => {
    if (isDrawer) {
      return null
    }

    return (
      <div
        className={cx(s.tooltip, {
          [s.promoTooltipOpen]: isOpenCampaign || isOpenCoupon,
          [s.openCampaign]: isOpenCampaign,
          [s.openCoupon]: isOpenCoupon,
        })}>
        {
          campaign?.campaigns?.map((item, index) => (
            <div className={cx(s.tooltipContainer, s.promoTooltip)}>
              <div className={s.promoInfoIcon}></div>
              <div className={s.tooltipContent}>
                {item?.campaignName}
              </div>
            </div>
          ))
        }
      </div>
    );
  }

  const handleMouseEnter = (cb) => {
    if (isDrawer) {
      return
    }

    const delayTime = 300;
    timerRef.current = setTimeout(() => {
      cb(true)
    }, delayTime)
  }

  const handleMouseLeave = (cb) => {
    if (isDrawer) {
      return
    }

    cb(false)

    clearTimeout(timerRef.current);
  }

  const handleAddToCartClick = async(item) => {
    sendAddToCartClickEvent(item)
    await yieldToMain()
    if (process.env.BROWSER && window?.Checkout?.AddToCart) {
      window.Checkout.AddToCart({
        listingId: item?.listingId || '',
        sku: sku || '',
        imageUrl: imageUrl || '',
        name: name || '',
        merchantName: item?.merchantName || '',
        continueShoppingLink: `/${categoryUrlKeyword}-c-${categoryId}`,
        quantity: 1,
      },
      'SfProductList',
      {
        callback: () => {
          dispatch(modalActionCreators.closeModal(MODAL_IDS.OTHER_MERCHANTS_MODAL))
        },
        errorHandler: () => {
          console.error('Checkout Voltran Add To Cart Error');
          dispatch(modalActionCreators.openModal(MODAL_IDS.OTHER_MERCHANTS_MODAL))
        },
      })
    }
  }

  const sendAddToCartClickEvent = (item) => {
    const params = new URLSearchParams(window?.location?.search);

    let listingsData = allListings?.map((listing) => ({
      ...listing,
      merchant_name: listing?.merchantName || '',
      due_date: listing?.shipmentDay || '',
      merchant_rating: listing?.ratingSummary?.lifetimeRating || '',
      price_3: null,
      price_2: getPrice(listing)?.prevPriceRaw || '',
      listing_id: listing.listingId || '',
      buybox_order: listing.buyboxOrder || '',
      price_1: getPrice(listing)?.currentPriceRaw || '', 
      campaign_text: null,
      coupon_count: null,
      coupons: [],
      estimated_arrival_date: null
    })) || [];

    window?.hepsiBus?.track(DAVINCI_EVENT_NAMES.ADD_TO_CART_CLICK, {
      canonical_url: window?.utagData ? window.utagData.canonical_url : '',
      category: `${categoryId} - ${categoryName}`,
      category_hierarchy: categories?.map(item => ({
        id: item?.categoryId || '',
        name: item?.categoryName || ''
      })),
      category_status: '',
      experiments: generateExperiments(),
      listings: listingsData,
      location: 'Allsellers',
      merchant: item?.merchantName || '',
      merchantName: item?.merchantName || '',
      merchantId: item?.merchantId,
      page_type: pageTypeForDavinci,
      page_value: sku,
      products: [{
        campaignId: '',
        id: productId || '',
        listingId: item?.listingId || '',
        mainProduct: false,
        merchantId: item?.merchantId,
        name: item?.name,
        position: 1,
        price: `${getProductFinalPrice(item?.prices)}`,
        quantity: 1,
        sku: sku || '',
        productId: sku || ''
      }],
      customerReviewCount: reviewCount || '',
      review_rate: reviewRate || '',
      ...(params?.has?.('tracking_cid') && {
        tracking_cid: params.get('tracking_cid')
      })
    }, generateExperiments())
  }

  return (
    <div className={cx(s.root, {[s.hasBorder]: listings.length > 1}, s[type], {[s.isDrawer]: isDrawer})}>
      <div className={s.content}>
        <div>
          <div className={s.merchantInfo}>
            <a className={s.merchantName} href={`/magaza/${listing.merchantInfo?.urlPostfix}`} title={listing?.merchantName} data-test-id={'merchant-name'}>
              {listing.merchantName}
            </a>
            {
              listing?.merchantInfo?.ratingSummary?.lifetimeRating > 0 && (
                <span
                  className={cx(s.rating, s.ratingTooltip, s[`rating${Math.floor(listing?.merchantInfo?.ratingSummary?.lifetimeRating)}`])}
                  data-test-id={'merchant-rating'}>
                  {parseFloat(listing?.merchantInfo?.ratingSummary?.lifetimeRating).toFixed(1).
                    replace('.', ',')}
                  {tooltipStaticData()}
                </span>
              )
            }
            {
              Boolean(listing?.merchantInfo?.merchantLabels?.length > 0) && (
                <div className={s.label} data-test-id={'merchant-label'}>
                  <i className={s.trustedIcon}/>
                  {tooltipStaticData()}
                </div>
              )
            }
          </div>

          <div className={s.shipmentContent} data-test-id={'shipment-content'}>
            <div
              className={s.dueText}
              dangerouslySetInnerHTML={{__html: deliveryOption?.deliveryOptions?.[0]?.text}}
              data-test-id={'shipment-text'}/>
          </div>

          <div className={s.campaignInfo}>
            {
              coupon?.couponCount > 0 &&
              <div
                className={cx(s.campaignInformation, {[s.couponOpen]: isOpenCoupon})}
                onMouseEnter={() => handleMouseEnter(setIsOpenCoupon)}
                onMouseLeave={() => handleMouseLeave(setIsOpenCoupon)}>
                <div className={cx(s.campaignInformationCount, {
                  [s.green]: false,
                  [s.orange]: true
                })}>{coupon?.couponCount}</div>
                <div className={s.campaignInformationText}>{'Kupon'}</div>
              </div>
            }
            {
              campaign?.campaignCount > 0 &&
              <div
                className={cx(s.campaignInformation, {[s.campaignOpen]: isOpenCampaign})}
                onMouseEnter={() => handleMouseEnter(setIsOpenCampaign)}
                onMouseLeave={() => handleMouseLeave(setIsOpenCampaign)}>
                <div className={cx(s.campaignInformationCount, {
                  [s.green]: true,
                  [s.orange]: false
                })}>{campaign?.campaignCount}</div>
                <div className={s.campaignInformationText}>{'Kampanya'}</div>
                {isOpenCampaign && tooltipCampaign()}
              </div>
            }
          </div>

          <div className={s.recomendationMerchantInfo}>
            <Price className={s.price} prefix={appConfig.cosmosPrefix} {...getPrice(listing)}/>
            {
              !isDrawer &&
              <Button
                prefix={appConfig.cosmosPrefix} className={s.button} kind={'ghost'} size={'micro'}
                onClick={() => handleMerchantProduct(listing?.merchantName)}>
                {'Ürüne git'}
              </Button>
            }
          </div>
        </div>

        <div>
          {
            isDrawer &&
            <>
              <Button
                prefix={appConfig.cosmosPrefix} className={s.button} size={'micro'}
                onClick={() => handleAddToCartClick(listing)}>
                {'Sepete ekle'}
              </Button>
              <Button
                prefix={appConfig.cosmosPrefix} className={cx(s.button, s.goToMerchant)} size={'micro'}
                onClick={() => handleMerchantProduct(listing?.merchantName)}>
                {'Ürüne git'}
              </Button>

            </>
          }
        </div>
      </div>
    </div>
  )
}

export default OtherMerchantBox
